import { useState } from 'react';
import { EyeIcon, Loader2Icon, EllipsisVerticalIcon, PencilIcon } from 'lucide-react';
import { DOCUMENT_KIND_TO_MODEL_KIND } from '@shared/schemas/document-model';
import type { DocumentGenerationKindDTO } from '@shared/schemas/document-generation';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useChatToolInteraction } from '../chat-tool-interaction';
import { getChatErrorMessage } from '../chat-errors';
import { downloadRemoteFile } from '../download-remote-file';
import { EXPORT_FORMAT_LABELS, getExportFormats, type ExportFormat } from '../document-canvas/canvas-config';
import { createDocumentGenerationExport } from '../services/document-generation.api';
import { DocumentGenerationKindIcon } from './ChatDocumentKindIcon';

type DocumentGenerationCardMenuProps = {
  /** Version courante : requise pour proposer la modification (moteur blocks). */
  currentVersion?: { generatedDocumentId: string; generationId: string; revisionNumber: number };
  conversationId: string;
  disabled?: boolean;
  /** Seul le moteur blocks sait exporter dans les autres formats de sa famille et modifier n'importe quelle version. */
  isBlocks: boolean;
  kind: DocumentGenerationKindDTO | null;
  onOpenPreview: () => void;
  title: string;
  triggerClassName?: string;
};

/** Menu « ⋮ » de la carte : aperçu, exports de la famille (blocks) et modification de la version. */
export function DocumentGenerationCardMenu({
  conversationId,
  currentVersion,
  disabled,
  isBlocks,
  kind,
  onOpenPreview,
  title,
  triggerClassName,
}: DocumentGenerationCardMenuProps) {
  const { beginDocumentRevision } = useChatToolInteraction();
  const [pendingFormat, setPendingFormat] = useState<ExportFormat | null>(null);
  // Le format natif est déjà servi par « Télécharger » : on propose les autres formats de la famille.
  const exportFormats = isBlocks && currentVersion && kind
    ? getExportFormats(DOCUMENT_KIND_TO_MODEL_KIND[kind]).filter((format) => format !== kind)
    : [];

  async function handleExport(format: ExportFormat) {
    if (!currentVersion || pendingFormat) {
      return;
    }
    setPendingFormat(format);
    try {
      const exported = await createDocumentGenerationExport(
        conversationId,
        currentVersion.generationId,
        { format },
      );
      await downloadRemoteFile(exported.downloadUrl, exported.filename);
    } catch (error) {
      toast.error(getChatErrorMessage(error));
    } finally {
      setPendingFormat(null);
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          aria-label={`Plus d’actions pour ${title}`}
          className={triggerClassName}
          disabled={disabled}
          size="icon"
          type="button"
          variant="secondary"
        >
          {pendingFormat
            ? <Loader2Icon className="size-4 animate-spin" aria-hidden />
            : <EllipsisVerticalIcon className="size-[1.125rem]" aria-hidden />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-auto min-w-52">
        <DropdownMenuItem onSelect={onOpenPreview}>
          <EyeIcon aria-hidden />
          Ouvrir l’aperçu
        </DropdownMenuItem>
        {exportFormats.length > 0 ? (
          <>
            <DropdownMenuSeparator />
            {exportFormats.map((format) => (
              <DropdownMenuItem
                disabled={Boolean(pendingFormat)}
                key={format}
                onSelect={() => void handleExport(format)}
              >
                <DocumentGenerationKindIcon kind={format} size="xs" />
                Exporter en {EXPORT_FORMAT_LABELS[format]}
              </DropdownMenuItem>
            ))}
          </>
        ) : null}
        {isBlocks && currentVersion ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => beginDocumentRevision({
                baseGenerationId: currentVersion.generationId,
                generatedDocumentId: currentVersion.generatedDocumentId,
                title,
                version: currentVersion.revisionNumber,
              })}
            >
              <PencilIcon aria-hidden />
              Modifier cette version
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
